import { FC, useMemo, useState } from 'react'
import { Draggable, Droppable } from 'react-beautiful-dnd'
import {
  Code as CodeIcon,
  DragIndicator,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  DataObject as DataObjectIcon,
} from '@mui/icons-material'
import { Box, Collapse, Divider, IconButton, Paper, Typography } from '@mui/material'

import { ObjectFieldDTO } from '@types'
import { Variable } from '@mobileTypes'

import { DraggableFieldItem } from './DraggableFieldItem'

interface AvailableFieldsPanelProps {
  objectFields: Array<
    ObjectFieldDTO & { commandKey: string; commandName: string; objectCode: string }
  >
  variables?: Variable[]
}

export const AvailableFieldsPanel: FC<AvailableFieldsPanelProps> = ({ objectFields, variables = [] }) => {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [variablesExpanded, setVariablesExpanded] = useState(true)

  // Группируем поля по objectCode
  const groupedFields = useMemo(() => {
    const groups: Record<
      string,
      {
        commandKeys: string[]
        fields: Array<
          ObjectFieldDTO & { commandKey: string; commandName: string; objectCode: string }
        >
      }
    > = {}

    objectFields.forEach(field => {
      const groupKey = field.objectCode
      if (!groups[groupKey]) {
        groups[groupKey] = {
          commandKeys: [],
          fields: [],
        }
        // По умолчанию группы свёрнуты
        setExpandedGroups(prev => ({ ...prev, [groupKey]: false }))
      }
      if (!groups[groupKey].commandKeys.includes(field.commandKey)) {
        groups[groupKey].commandKeys.push(field.commandKey)
      }
      groups[groupKey].fields.push(field)
    })

    return groups
  }, [objectFields])

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }))
  }

  return (
    <Paper
      sx={{
        width: 300,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant='h6'>Available Fields</Typography>
        <Typography variant='caption' color='text.secondary'>
          Drag fields to configure rows
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        <Droppable droppableId='NEW_FIELD' isDropDisabled type='ELEMENTS'>
          {provided => (
            <Box ref={provided.innerRef} {...provided.droppableProps}>
              <Draggable draggableId='NEW:FIELD' index={0}>
                {(providedDrag, snapshotDrag) => (
                  <Box
                    ref={providedDrag.innerRef}
                    {...providedDrag.draggableProps}
                    {...providedDrag.dragHandleProps}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 1.5,
                      px: 2,
                      gap: 2,
                      mb: 1,
                      backgroundColor: snapshotDrag.isDragging ? 'primary.light' : 'primary.main',
                      color: 'white',
                      borderRadius: 1,
                      cursor: 'grab',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <DragIndicator fontSize='small' />
                    <CodeIcon fontSize='small' />
                    <Typography variant='body2' sx={{ fontWeight: 500, flex: 1 }}>
                      Field
                    </Typography>
                  </Box>
                )}
              </Draggable>
              {provided.placeholder}
            </Box>
          )}
        </Droppable>

        <Divider sx={{ my: 2 }} />

        {/* Variables Section */}
        {variables.length > 0 && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
                borderRadius: 1,
                mb: 1,
              }}
              onClick={() => setVariablesExpanded(!variablesExpanded)}
            >
              <DataObjectIcon fontSize='small' sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography
                variant='overline'
                sx={{
                  flex: 1,
                  color: 'text.secondary',
                  fontWeight: 600,
                }}
              >
                Variables ({variables.length})
              </Typography>
              <IconButton size='small'>
                {variablesExpanded ? (
                  <ExpandLessIcon fontSize='small' />
                ) : (
                  <ExpandMoreIcon fontSize='small' />
                )}
              </IconButton>
            </Box>
            <Collapse in={variablesExpanded}>
              <Droppable droppableId='VARIABLES' isDropDisabled type='ELEMENTS'>
                {provided => (
                  <Box ref={provided.innerRef} {...provided.droppableProps}>
                    {variables.map((variable, index) => (
                      <Draggable
                        key={`VAR:${variable.internalId}`}
                        draggableId={`VAR:${variable.internalId}`}
                        index={index}
                      >
                        {(providedDrag, snapshotDrag) => (
                          <Box
                            ref={providedDrag.innerRef}
                            {...providedDrag.draggableProps}
                            {...providedDrag.dragHandleProps}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              py: 1,
                              px: 1.5,
                              gap: 1,
                              mb: 0.5,
                              backgroundColor: snapshotDrag.isDragging
                                ? 'secondary.light'
                                : 'background.paper',
                              border: '1px solid',
                              borderColor: snapshotDrag.isDragging ? 'secondary.main' : 'divider',
                              borderRadius: 1,
                              cursor: 'grab',
                              '&:hover': {
                                borderColor: 'secondary.main',
                                backgroundColor: 'action.hover',
                              },
                              transition: 'all 0.2s',
                            }}
                          >
                            <DragIndicator fontSize='small' sx={{ color: 'text.secondary' }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant='body2'
                                sx={{
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {variable.internalId}
                              </Typography>
                              <Typography variant='caption' color='text.secondary'>
                                {variable.variableType === 'multiple' ? 'Array' : 'Single'} • {variable.valueType || 'any'}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </Collapse>
            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        {/* Object Fields Section */}
        {Object.keys(groupedFields).length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              No commands configured
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Add commands to see available fields
            </Typography>
          </Box>
        ) : (
          Object.entries(groupedFields).map(([objectCode, group], groupIndex) => {
            const isExpanded = expandedGroups[objectCode] !== false

            return (
              <Box key={objectCode}>
                {groupIndex > 0 && <Divider sx={{ my: 2 }} />}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                    borderRadius: 1,
                  }}
                  onClick={() => toggleGroup(objectCode)}
                >
                  <Typography
                    variant='overline'
                    sx={{
                      flex: 1,
                      color: 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    {objectCode} ({group.fields.length})
                  </Typography>
                  <IconButton size='small'>
                    {isExpanded ? (
                      <ExpandLessIcon fontSize='small' />
                    ) : (
                      <ExpandMoreIcon fontSize='small' />
                    )}
                  </IconButton>
                </Box>
                <Collapse in={isExpanded}>
                  {/* Для каждого commandKey в группе создаем отдельный Droppable */}
                  {group.commandKeys.map(commandKey => {
                    const fieldsForCommand = group.fields.filter(f => f.commandKey === commandKey)

                    return (
                      <Droppable
                        key={commandKey}
                        droppableId={`FIELDS:${commandKey}`}
                        isDropDisabled
                        type='ELEMENTS'
                      >
                        {provided => (
                          <Box ref={provided.innerRef} {...provided.droppableProps}>
                            {fieldsForCommand.map((field, index) => (
                              <DraggableFieldItem
                                key={`${field.commandKey}:${field.name}`}
                                field={field}
                                index={index}
                              />
                            ))}
                            {provided.placeholder}
                          </Box>
                        )}
                      </Droppable>
                    )
                  })}
                </Collapse>
              </Box>
            )
          })
        )}
      </Box>
    </Paper>
  )
}
